import React, { useEffect, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    FormControlLabel,
    Link,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import AppDialog from '../../dialog/AppDialog'

export const IMPORT_MODES = { APPEND: 'append', REPLACE: 'replace' }
export const ACCEPTED_EXTENSIONS = ['xlsx', 'xls', 'csv']

const MODE_OPTIONS = [
    {
        value: IMPORT_MODES.APPEND,
        label: 'Add new rows',
        description: 'Keeps every current row. File rows that already exist in the table are ignored.',
    },
    {
        value: IMPORT_MODES.REPLACE,
        label: 'Replace the whole table',
        description: 'Deletes every current row, then imports the rows of the file.',
    },
]

const optionStyles = (selected, danger) => ({
    m: 0,
    px: 1.5,
    py: 1,
    alignItems: 'flex-start',
    border: 1,
    borderRadius: 1,
    borderColor: selected ? (danger ? 'error.main' : 'info.main') : 'divider',
    bgcolor: (theme) => (selected ? alpha(theme.palette[danger ? 'error' : 'info'].main, 0.06) : 'transparent'),
    '& .MuiRadio-root': { mt: -0.5 },
})

export const hasAcceptedExtension = (fileName = '') =>
    ACCEPTED_EXTENSIONS.includes(fileName.split('.').pop())

export default function ImportModeDialog({ open, tableLabel, onClose, onConfirm, onShowHelp }) {
    const [mode, setMode] = useState(IMPORT_MODES.APPEND)
    const [file, setFile] = useState(null)
    const [fileError, setFileError] = useState('')
    const replacing = mode === IMPORT_MODES.REPLACE

    useEffect(() => {
        if (!open) return
        setMode(IMPORT_MODES.APPEND)
        setFile(null)
        setFileError('')
    }, [open])

    const selectFile = (event) => {
        const selected = event.target.files?.[0]
        event.target.value = null
        if (!selected) return
        if (!hasAcceptedExtension(selected.name)) {
            setFile(null)
            setFileError('Invalid file: select an .xlsx, .xls or .csv file.')
            return
        }
        setFileError('')
        setFile(selected)
    }

    return (
        <AppDialog
            open={open}
            onClose={onClose}
            title={`Upload new prices – ${tableLabel}`}
            maxWidth='sm'
            showCloseButton
            actions={
                <>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        variant='contained'
                        color={replacing ? 'error' : 'info'}
                        disabled={!file}
                        onClick={() => onConfirm(file, mode)}>
                        {replacing ? 'Replace table' : 'Add rows'}
                    </Button>
                </>
            }>
            <Stack spacing={2.5}>
                <Box>
                    <Typography component='h3' variant='subtitle2' id='import-mode-label' gutterBottom>
                        1. What should the import do?
                    </Typography>
                    <RadioGroup aria-labelledby='import-mode-label' value={mode} onChange={(e) => setMode(e.target.value)} sx={{ gap: 1 }}>
                        {MODE_OPTIONS.map((option) => (
                            <FormControlLabel
                                key={option.value}
                                value={option.value}
                                control={<Radio color={option.value === IMPORT_MODES.REPLACE ? 'error' : 'info'} />}
                                sx={optionStyles(mode === option.value, option.value === IMPORT_MODES.REPLACE)}
                                label={
                                    <Box>
                                        <Typography fontWeight={600}>{option.label}</Typography>
                                        <Typography variant='body2' color='text.secondary'>
                                            {option.description}
                                        </Typography>
                                    </Box>
                                }
                            />
                        ))}
                    </RadioGroup>
                    {replacing && (
                        <Alert severity='warning' sx={{ mt: 1.5 }}>
                            Rows absent from the file will be permanently removed. Download current prices first to keep a
                            backup.
                        </Alert>
                    )}
                </Box>

                <Box>
                    <Typography component='h3' variant='subtitle2' gutterBottom>
                        2. Choose the file
                    </Typography>
                    <Stack direction='row' spacing={1.5} alignItems='center' flexWrap='wrap' useFlexGap>
                        <Button component='label' variant='outlined' color='inherit' startIcon={<AttachFileIcon />}>
                            {file ? 'Change file' : 'Choose file'}
                            <input type='file' hidden accept='.xlsx,.xls,.csv' aria-label='Price file' onChange={selectFile} />
                        </Button>
                        <Typography variant='body2' color={file ? 'text.primary' : 'text.secondary'} noWrap sx={{ minWidth: 0 }}>
                            {file ? file.name : 'No file selected (.xlsx, .xls or .csv)'}
                        </Typography>
                    </Stack>
                    {fileError && (
                        <Alert severity='error' sx={{ mt: 1.5 }}>
                            {fileError}
                        </Alert>
                    )}
                    <Typography variant='body2' color='text.secondary' sx={{ mt: 1.5 }}>
                        The first row is treated as the header. Need the expected columns?{' '}
                        <Link component='button' type='button' variant='body2' onClick={onShowHelp}>
                            File format help
                        </Link>
                    </Typography>
                </Box>
            </Stack>
        </AppDialog>
    )
}
